import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quiz() {
  const { t } = useLanguage();
  const questions = t("quiz.questions");

  const [step, setStep] = useState(-1); // -1 = intro, 0..N = questions, N = form, N+1 = success
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);

  const total = questions.length;
  const isQuestion = step >= 0 && step < total;
  const isForm = step === total;
  const isSuccess = step === total + 1;

  const progress = Math.max(
    0,
    Math.min(100, Math.round(((Math.max(step, 0) + 1) / (total + 1)) * 100))
  );

  const select = (q, opt) => {
    setAnswers((a) => ({ ...a, [q.id]: { question: q.q, answer: opt, question_id: q.id } }));
    setTimeout(() => setStep((s) => s + 1), 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/quiz`, {
        ...form,
        answers: Object.values(answers),
      });
      setStep(total + 1);
      toast.success(t("quiz.toast_sent"));
    } catch (err) {
      toast.error(t("quiz.toast_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quiz"
      data-testid="quiz-section"
      className="relative py-24 md:py-36 bg-[#F7F5F0]"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="eyebrow text-ink mb-4">
              {t("quiz.overline")} · 04
            </div>
            <h2 className="heading-display text-5xl md:text-6xl" data-testid="quiz-title">
              {t("quiz.title")}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
            <p className="text-base md:text-lg text-ink leading-relaxed">
              {t("quiz.sub")}
            </p>
          </div>
        </div>

        <div
          data-testid="quiz-card"
          className="border border-navy bg-[#FFFFFF] p-8 md:p-14 min-h-[440px] relative overflow-hidden"
        >
          {/* Progress bar */}
          {(isQuestion || isForm) && (
            <>
              <div className="flex items-center justify-between mb-10">
                <div className="eyebrow text-ink">
                  {t("quiz.step")} {Math.min(step + 1, total + 1)} {t("quiz.of")}{" "}
                  {total + 1}
                </div>
                <div className="eyebrow text-ink">{progress}%</div>
              </div>
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#E5E3DB]">
                <div
                  className="h-full bg-navy transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </>
          )}

          {step === -1 && (
            <div data-testid="quiz-intro" className="py-6">
              <p className="heading-display text-3xl md:text-4xl max-w-[720px] mb-8">
                {t("quiz.title")}
              </p>
              <button
                onClick={() => setStep(0)}
                data-testid="quiz-start-btn"
                className="btn-editorial btn-editorial-primary"
              >
                {t("quiz.start")} <ArrowRight size={16} />
              </button>
            </div>
          )}

          {isQuestion && (
            <div data-testid={`quiz-question-${step}`}>
              <h3 className="heading-display text-3xl md:text-5xl mb-10 leading-tight max-w-[820px]">
                {questions[step].q}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {questions[step].options.map((opt, i) => {
                  const selected = answers[questions[step].id]?.answer === opt;
                  return (
                    <button
                      key={i}
                      onClick={() => select(questions[step], opt)}
                      data-testid={`quiz-option-${step}-${i}`}
                      className={`text-left px-6 py-5 border transition-all duration-200 ${
                        selected
                          ? "bg-navy text-[#F7F5F0] border-navy"
                          : "bg-[#F7F5F0] border-[#E5E3DB] hover:border-navy"
                      }`}
                    >
                      <span className="font-serif text-xl">{opt}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-10 flex justify-between items-center">
                <button
                  onClick={() => setStep((s) => Math.max(-1, s - 1))}
                  disabled={step === 0}
                  data-testid="quiz-back-btn"
                  className="btn-editorial btn-editorial-outline disabled:opacity-30"
                >
                  <ArrowLeft size={16} /> {t("quiz.back")}
                </button>
                <div className="eyebrow text-ink">
                  {step + 1} / {total}
                </div>
              </div>
            </div>
          )}

          {isForm && (
            <form onSubmit={submit} data-testid="quiz-form" className="max-w-xl">
              <h3 className="heading-display text-3xl md:text-4xl mb-3">
                {t("quiz.form_title")}
              </h3>
              <p className="text-ink mb-8">{t("quiz.form_sub")}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <LabeledInput
                  label={t("quiz.name")}
                  required
                  testid="quiz-name"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                />
                <LabeledInput
                  label={t("quiz.email")}
                  required
                  type="email"
                  testid="quiz-email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                />
                <LabeledInput
                  label={t("quiz.company")}
                  testid="quiz-company"
                  value={form.company}
                  onChange={(v) => setForm({ ...form, company: v })}
                />
                <LabeledInput
                  label={t("quiz.phone")}
                  testid="quiz-phone"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                />
              </div>

              <div className="mt-10 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStep(total - 1)}
                  className="btn-editorial btn-editorial-outline"
                  data-testid="quiz-form-back-btn"
                >
                  <ArrowLeft size={16} /> {t("quiz.back")}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="quiz-submit-btn"
                  className="btn-editorial btn-editorial-primary disabled:opacity-60"
                >
                  {submitting ? "…" : t("quiz.submit")}
                  <ArrowRight size={16} />
                </button>
              </div>
            </form>
          )}

          {isSuccess && (
            <div data-testid="quiz-success" className="py-10 max-w-xl">
              <CheckCircle2 size={44} className="mb-6 text-green-dark" />
              <h3 className="heading-display text-4xl md:text-5xl mb-4">
                {t("quiz.success_title")}
              </h3>
              <p className="text-ink text-lg">{t("quiz.success_sub")}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function LabeledInput({ label, required, type = "text", testid, value, onChange }) {
  return (
    <label className="flex flex-col">
      <span className="eyebrow text-ink mb-1">
        {label}
        {required && <span className="ml-1 text-navy">*</span>}
      </span>
      <input
        required={required}
        type={type}
        data-testid={`${testid}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-b border-navy/30 pb-2 focus:outline-none focus:border-navy"
      />
    </label>
  );
}
